import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import AlarmIcon from '../../assets/icons/alarm.svg?react';
import AddAlertIcon from '../../assets/icons/selection-add-alert.svg?react';
import GraphsIcon from '../../assets/icons/selection-graphs.svg?react';
import DownloadIcon from '../../assets/icons/download.svg?react';
import GraphIcon from '../../assets/icons/graph.svg?react';
import FilterIcon from '../../assets/icons/filter.svg?react';
import MachineIcon from '../../assets/icons/machine.svg?react';
import { ResponsiveDataView } from '../../components/data-display/ResponsiveDataView';
import { AddAlarmDialog } from '../../components/dialogs/AddAlarmDialog';
import { ActiveFilterTags } from '../../components/filters/ActiveFilterTags';
import { MobileFilters } from '../../components/filters/MobileFilters';
import { useMachineFilterTags } from '../../components/filters/useMachineFilterTags';
import { FiltersMobile } from '../../components/FiltersMobile';
import { AppBarIconButton } from '../../components/AppBarIconButton';
import { MachineSidePanel } from '../../components/machines';
import { MachineDetailPane } from '../../components/machines/MachineDetailPane';
import { MasterDetail } from '../../components/layout/MasterDetail';
import { useLayoutTier } from '../../hooks/useLayoutTier';
import { useSplitLayout } from '../../hooks/useSplitLayout';
import { MachineCard } from './MachineCard';
import { MachinesSearchBar } from './MachinesSearchBar';
import { MachineSelectionHeader } from './MachineSelectionHeader';
import { toMachineDetailPreview } from './machineDetailPreview';
import { useMachineViewMode } from './useMachineViewMode';
import { Toast, ToastProvider } from '../../components/Toast';
import { useHeader } from '../../contexts/HeaderContext';
import { useMobileFilter } from '../../contexts/MobileFilterContext';
import {
  Machine as GeneratedMachine,
  MachinesOrderByInput,
  SortOrder,
} from '../../gql/generated';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { Machine } from '../../types/machines/machine.types';
import { openMachineGraphs } from '../../utils/graphNavigation';
import { createMachineColumns } from './createMachineColumns';
import { getDefaultColumnVisibility } from './machineColumnConfig';
import { MachineListItemMobile } from './MachineListItemMobile';
import { MACHINE_TABLET_COLUMNS } from './machineTabletColumns';
import { TableMultiSelectActions } from './TableMultiSelectActions';
import { TableMultiSelectAction } from './types';
import { useMachineColumnFilters } from './useMachineColumnFilters';
import { useMachinesExport } from './useMachinesExport';
import { useMachinesTable } from './useMachinesTable';
import { cn } from '../../utils/className';
import { logger } from '../../utils/logger';
import {
  navigateWithTransition,
  usePageTransitionReady,
} from '../../utils/viewTransition';

export function MachinesPage() {
  const navigate = useNavigate();
  // Coming back from a detail page, the way back waits for this page.
  usePageTransitionReady();
  const { isPhone, isTablet, isTabletDown } = useLayoutTier();
  const state = useMachinesTable();
  const { hasActiveFilters, clearFilters } = useMachineFilters();
  const filterTags = useMachineFilterTags();
  const { setMobileActionButton, setPageTitle } = useHeader();
  const isWideEnoughToSplit = useSplitLayout();
  // Desktop keeps its table, so only a wide tablet splits.
  const showSplit = isTablet && isWideEnoughToSplit;
  const { view, views, setView } = useMachineViewMode();
  const { toggle, close } = useMobileFilter();
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  // The machine as it was clicked. The list's current copy replaces it below.
  const [clickedMachine, setClickedMachine] = useState<GeneratedMachine | null>(
    null
  );
  // One flag for every layout. A rotation that changes the layout moves the
  // open detail to the new one, instead of leaving it in the old one.
  const [detailOpen, setDetailOpen] = useState(false);
  const masterRef = useRef<HTMLElement>(null);
  const clearMachineTimer = useRef<number | undefined>(undefined);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>(
    'success'
  );
  const { exportMachines, exporting } = useMachinesExport();

  // Get the column filter render function (reads filters directly from URL)
  const renderColumnFilter = useMachineColumnFilters();

  // `state` is rebuilt on every render, so the header button reads the setter
  // through a ref. A direct dependency would rerun the effect below forever.
  const setSelectedMachineIdsRef = useRef(state.setSelectedMachineIds);
  useEffect(() => {
    setSelectedMachineIdsRef.current = state.setSelectedMachineIds;
  });

  // Leaving selection mode drops what was selected, so the bar goes with it.
  const handleSelectionModeChange = useCallback((active: boolean) => {
    if (!active) {
      setSelectedMachineIdsRef.current([]);
    }

    setSelectionMode(active);
  }, []);

  // Set up the header buttons for the phone and the tablet. Selection mode
  // fades them out: the bar under the search row carries its Cancel.
  useLayoutEffect(() => {
    setMobileActionButton(
      <div
        inert={selectionMode}
        className={cn(
          `flex items-center gap-3 transition-opacity duration-200 ease-in-out
          motion-reduce:transition-none`,
          selectionMode && 'opacity-0'
        )}
      >
        <button
          type='button'
          aria-label='Select machines'
          onClick={() => handleSelectionModeChange(true)}
          className='bg-inverted/10 hover:bg-inverted/20 size-p2 text-blue-050
            h-10.5 cursor-pointer rounded px-4 font-bold transition-all
            duration-150'
        >
          Select
        </button>
        <div className='relative'>
          <AppBarIconButton
            aria-label='Toggle filters'
            icon={<FilterIcon className='[&_path]:fill-white' />}
            onClick={toggle}
          />
          {hasActiveFilters && (
            <div
              className='bg-focus border-surface absolute top-0 right-0 h-2.5
                w-2.5 rounded-full border-2'
            />
          )}
        </div>
      </div>
    );

    return () => {
      setMobileActionButton(null);
    };
  }, [
    setMobileActionButton,
    toggle,
    hasActiveFilters,
    selectionMode,
    handleSelectionModeChange,
  ]);

  // The app bar names the page on the compact tiers, where the list header is
  // suppressed.
  useLayoutEffect(() => {
    if (!isTabletDown) return;

    setPageTitle('Machines');
    return () => setPageTitle(null);
  }, [isTabletDown, setPageTitle]);

  const columns = useMemo(
    () =>
      createMachineColumns({
        selectedMachineIds: state.selectedMachineIds,
        onSelectionChange: state.setSelectedMachineIds,
        onAddAlarm: (machineId) => {
          // Set selection to just this machine and open dialog
          state.setSelectedMachineIds([machineId]);
          setAlarmDialogOpen(true);
        },
      }),
    [state]
  );

  const handleClearFilters = () => {
    clearFilters();
    close();
  };

  // Helper to convert selected database IDs to machineIds
  const getSelectedMachineIds = (): string[] => {
    return state.machines
      .filter((machine) => state.selectedMachineIds.includes(machine.id))
      .map((machine) => machine.machineId);
  };

  // Keep the export sorted the same way the table is
  const exportOrderBy = useMemo<MachinesOrderByInput | undefined>(() => {
    const [firstSort] = state.sorting;
    if (!firstSort) return undefined;

    return {
      [firstSort.id]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
    } as MachinesOrderByInput;
  }, [state.sorting]);

  const handleExport = useCallback(
    async (machineIds?: string[]) => {
      try {
        const count = await exportMachines({
          machineIds,
          orderBy: exportOrderBy,
        });
        setToastMessage(
          count > 0
            ? `✓ Exported ${count} machine${count === 1 ? '' : 's'} to CSV`
            : 'No machines matched the current filters'
        );
        setToastVariant(count > 0 ? 'success' : 'error');
      } catch (error) {
        logger.error('Failed to export machines:', error);
        setToastMessage('Could not export machines. Please try again.');
        setToastVariant('error');
      }
      setToastOpen(true);
    },
    [exportMachines, exportOrderBy]
  );

  // CSV export is desktop-only, so it is not part of the shared actions
  const sharedSelectActions: TableMultiSelectAction[] = [
    {
      label: 'Show graphs',
      onClick: () => openMachineGraphs(getSelectedMachineIds()),
      icon: <GraphIcon />,
    },
    {
      label: 'Add Pulse alert',
      onClick: () => setAlarmDialogOpen(true),
      icon: <AlarmIcon />,
      variant: 'primary',
    },
  ];

  const multiSelectActions: TableMultiSelectAction[] = [
    {
      label: exporting ? 'Exporting...' : 'Export selection (CSV)',
      onClick: () => handleExport(state.selectedMachineIds),
      icon: <DownloadIcon />,
    },
    ...sharedSelectActions,
  ];

  // The phone and tablet bar puts the alert first, with its own icons.
  const mobileSelectionActions = [
    {
      label: 'Add alert',
      onClick: () => setAlarmDialogOpen(true),
      icon: <AddAlertIcon />,
    },
    {
      label: 'Show graphs',
      onClick: () => openMachineGraphs(getSelectedMachineIds()),
      icon: <GraphsIcon />,
    },
  ];

  // A refetch or a filter change replaces the rows, so the detail reads the
  // list's current copy of the machine.
  const listedMachine = clickedMachine
    ? state.machines.find((machine) => machine.id === clickedMachine.id)
        ?._original
    : undefined;
  const detailMachine = listedMachine ?? clickedMachine;

  // A compact tier with no room to split gets the detail as a page of its own.
  const detailIsPage = isTabletDown && !showSplit;
  const overlayOpen = detailOpen && !isTabletDown;
  // A filter change can drop the machine from the list. The pane then closes,
  // so it never describes a row the list does not show, and stays closed when
  // a later change brings the machine back. The split pane has no close
  // animation, so the machine clears at once.
  const machineLeftList = !state.loading && !listedMachine;
  if (showSplit && detailOpen && machineLeftList) {
    setDetailOpen(false);
    setClickedMachine(null);
  }
  const splitMachine = showSplit && detailOpen ? detailMachine : null;

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    // Clear the machine after the side panel's close animation (300ms).
    window.clearTimeout(clearMachineTimer.current);
    clearMachineTimer.current = window.setTimeout(
      () => setClickedMachine(null),
      350
    );
  }, []);

  // A tablet rotated to portrait, with the detail open beside the list, moves
  // the detail to its own page.
  useEffect(() => {
    if (detailOpen && detailIsPage && clickedMachine) {
      navigate(`/machines/${clickedMachine.id}`, {
        state: { preview: toMachineDetailPreview(clickedMachine) },
      });
    }
  }, [detailOpen, detailIsPage, clickedMachine, navigate]);

  const handleRowClick = (machine: Machine) => {
    if (!machine._original) return;

    if (detailIsPage) {
      const original = machine._original;
      navigateWithTransition('push', () =>
        navigate(`/machines/${original.id}`, {
          state: { preview: toMachineDetailPreview(original) },
        })
      );
      return;
    }

    // A row opened within the close animation must not lose its machine.
    window.clearTimeout(clearMachineTimer.current);
    setClickedMachine(machine._original);
    setDetailOpen(true);
  };

  const handleSidePanelClose = (open: boolean) => {
    if (!open) closeDetail();
  };

  // Focus goes back to the row that opened the pane. Without this it drops to
  // the body, and a keyboard user starts again from the top of the page.
  const handleSplitClose = () => {
    const activeRow = masterRef.current?.querySelector<HTMLElement>(
      '[aria-current="true"]'
    );

    closeDetail();
    activeRow?.focus();
  };

  const handleAlarmCreated = useCallback(
    (alarmId: string) => {
      // Navigate to alarms page with highlight parameter
      navigate(`/alarms?highlight=${alarmId}`);
    },
    [navigate]
  );

  const activeMachineId = splitMachine?.id;

  const dataView = (
    <ResponsiveDataView
      data={state.machines}
      isLoading={state.loading}
      onRefresh={async () => {
        await state.refetch();
      }}
      error={state.error}
      getRowId={(machine) => machine.id}
      columns={columns}
      pageIndex={state.pageIndex}
      pageSize={state.pageSize}
      onPageChange={state.setPageIndex}
      onPageSizeChange={state.setPageSize}
      sorting={state.sorting}
      onSortingChange={state.setSorting}
      columnPinning={state.columnPinning}
      onColumnPinningChange={state.setColumnPinning}
      columnVisibility={state.columnVisibility}
      onColumnVisibilityChange={state.setColumnVisibility}
      defaultColumnVisibility={getDefaultColumnVisibility()}
      columnOrder={state.columnOrder}
      onColumnOrderChange={state.setColumnOrder}
      columnSizing={state.columnSizing}
      onColumnSizingChange={state.onColumnSizingChange}
      resetColumnSizing={state.resetColumnSizing}
      renderListItem={(machine, config) => (
        <MachineListItemMobile machine={machine} config={config} />
      )}
      enableSelection
      selectedIds={state.selectedMachineIds}
      onSelectionChange={state.setSelectedMachineIds}
      selectionMode={isTabletDown ? selectionMode : undefined}
      onSelectionModeChange={
        isTabletDown ? handleSelectionModeChange : undefined
      }
      onRowClick={handleRowClick}
      totalPages={state.totalPages}
      totalCount={state.totalCount}
      columnFilters={renderColumnFilter}
      activeFilters={
        hasActiveFilters ? (
          <ActiveFilterTags tags={filterTags} onClearAll={clearFilters} />
        ) : undefined
      }
      multiSelectActions={
        <TableMultiSelectActions
          selectedCount={state.selectedMachineIds.length}
          actions={multiSelectActions}
          onClearSelection={() => state.setSelectedMachineIds([])}
        />
      }
      mobileSelectionActions={mobileSelectionActions}
      enableManageColumns
      emptyStateMessage='No machines found. Try adjusting your filters.'
      ariaLabel='Machines'
      title={isTabletDown ? undefined : 'Machines'}
      icon={<MachineIcon />}
      activeId={activeMachineId}
      compact={{
        layout: view,
        toolbar: (
          <>
            <MachinesSearchBar
              view={view}
              views={views}
              onViewChange={setView}
            />
            <MachineSelectionHeader
              open={selectionMode}
              selectedCount={state.selectedMachineIds.length}
              onCancel={() => handleSelectionModeChange(false)}
            />
          </>
        ),
        renderGridItem: (machine, config) => (
          <MachineCard machine={machine} config={config} />
        ),
        // A phone pane stays under 30rem, where the shared tracks give one
        // column. A gallery needs two.
        gridClassName: isPhone ? 'grid-cols-2 @[48rem]:grid-cols-3' : undefined,
        tableColumns: MACHINE_TABLET_COLUMNS,
        getRowLabel: (machine) =>
          machine.displayName || machine.machineId || 'machine',
      }}
    />
  );

  return (
    <ToastProvider>
      {/* Machine Side Panel */}
      <MachineSidePanel
        open={overlayOpen}
        onOpenChange={handleSidePanelClose}
        machine={detailMachine}
      />

      <Toast
        open={toastOpen}
        onOpenChange={setToastOpen}
        description={toastMessage}
        variant={toastVariant}
      />

      {/* Alarm Dialog */}
      <AddAlarmDialog
        open={alarmDialogOpen}
        onOpenChange={setAlarmDialogOpen}
        selectedMachineIds={state.selectedMachineIds}
        availableMachines={state.machines.map((m) => ({
          id: m.id,
          displayName: m.displayName || m.machineId,
        }))}
        onAlarmCreated={handleAlarmCreated}
      />

      {/* Mobile Filter Panel */}
      <FiltersMobile
        onClear={handleClearFilters}
        resultCount={state.totalCount}
      >
        <MobileFilters />
      </FiltersMobile>

      {/* One tree for every layout, so the list never remounts and keeps its
          scroll position when the pane opens or closes. */}
      <MasterDetail
        masterRef={masterRef}
        master={dataView}
        detail={
          splitMachine ? (
            <MachineDetailPane
              machine={splitMachine}
              showHeader
              onClose={handleSplitClose}
            />
          ) : undefined
        }
      />
    </ToastProvider>
  );
}
